import React from "react";
import BaseTile from "../../../components/Tile/BaseTile";
import { TileHeaderButton } from "../../../components/Tile/TileHeader";
import RefreshButton from "../../../components/Tile/RefreshButton";
import TileContent, { TileStatusContent } from "../../../components/Tile/TileContent";
import ConformanceSetup from "../ConformanceChecker/components/ConformanceSetup";
import ReferenceFilePickerModal from "../ConformanceChecker/components/ReferenceFilePickerModal";
import VariantSelectionModal from "./components/VariantSelectionModal";
import SplitGraphView from "./components/SplitGraphView";
import { useVisualConformance } from "./hooks/useVisualConformance";

const VisualConformanceChecker = ({ activeFile, onGetHeaderActions }) => {
  const {
    phase,
    objectType,
    setObjectType,
    objectTypes,
    zeta,
    setZeta,
    profilingMode,
    setProfilingMode,
    referenceFile,
    referenceKey,
    uploadingRef,
    uploadError,
    handleReferenceFileChange,
    availableFiles,
    showFilePicker,
    openFilePicker,
    closeFilePicker,
    handleSelectExistingFile,
    startSelecting,
    loadingRefVariants,
    referenceVariants,
    referenceVariantsMeta,
    selectedReferenceIds,
    onToggleReference,
    observedHook,
    selectedObservedIds,
    onToggleObserved,
    runVisualCheck,
    rerunTemporal,
    loadingCheck,
    loadingTemporal,
    checkError,
    visualResult,
    sequentialResult,
    temporalResult,
    reset,
  } = useVisualConformance(activeFile);

  const isLoading = loadingRefVariants || loadingCheck;

  // Reset button — registered into the shared tile chrome header (GridComponent)
  // instead of BaseTile's own header, so it doesn't add a row that pushes content down.
  const headerActions = React.useMemo(() => {
    if (phase === "setup" && !checkError) return null;
    return <RefreshButton onClick={reset} size="sm" title="Reset to start" />;
  }, [phase, checkError, reset]);

  React.useEffect(() => {
    if (onGetHeaderActions) {
      onGetHeaderActions(headerActions);
    }
  }, [onGetHeaderActions, headerActions]);

  return (
    <>
      {/* Main tile — shows setup or results */}
      <BaseTile
        className="relative"
        loading={isLoading && phase !== "selecting"}
        error={checkError}
        errorAction={
          <TileHeaderButton onClick={reset} variant="primary" size="sm">
            Back to Start
          </TileHeaderButton>
        }
        minHeight="auto"
        style={{ height: "auto" }}
      >
        <TileContent layout="flex" direction="column" grow scroll="y" className="min-h-0">
          {/* Setup phase */}
          {phase === "setup" && (
            <div className="flex flex-col items-center flex-1 py-4">
              <ConformanceSetup
                objectType={objectType}
                setObjectType={setObjectType}
                objectTypes={objectTypes}
                referenceFile={referenceFile}
                referenceKey={referenceKey}
                uploadingRef={uploadingRef}
                uploadError={uploadError}
                onReferenceFileChange={handleReferenceFileChange}
                onOpenFilePicker={openFilePicker}
                onRun={startSelecting}
                loading={loadingRefVariants}
                runLabel="Select Variants"
              />

            </div>
          )}

          {/* Loading reference variants (brief transition) */}
          {phase === "setup" && loadingRefVariants && (
            <TileStatusContent
              status="loading"
              title="Loading Reference Variants"
              message="Extracting variants from reference OCEL…"
            />
          )}

          {/* Results phase */}
          {phase === "results" && visualResult && (
            <div className="flex flex-col">
              <SplitGraphView
                visualResult={visualResult}
                sequentialResult={sequentialResult}
                temporalResult={temporalResult}
                onReset={reset}
                profilingMode={profilingMode}
                setProfilingMode={setProfilingMode}
                zeta={zeta}
                setZeta={setZeta}
                onRerunTemporal={rerunTemporal}
                loadingTemporal={loadingTemporal}
              />
            </div>
          )}

          {/* Variant selection phase — rendered inside tile like setup */}
          {phase === "selecting" && !loadingCheck && (
            <VariantSelectionModal
              referenceVariants={referenceVariants}
              referenceVariantsMeta={referenceVariantsMeta}
              selectedReferenceIds={selectedReferenceIds}
              onToggleReference={onToggleReference}
              observedHook={observedHook}
              selectedObservedIds={selectedObservedIds}
              onToggleObserved={onToggleObserved}
              onStart={runVisualCheck}
              onCancel={reset}
              loadingCheck={loadingCheck}
            />
          )}

          {/* Loading while check runs */}
          {loadingCheck && (
            <TileStatusContent
              status="loading"
              title="Running Visual Conformance Check"
              message="Building activity graphs and computing conformance mapping…"
            />
          )}
        </TileContent>

        {showFilePicker && (
          <ReferenceFilePickerModal
            files={availableFiles}
            onSelect={handleSelectExistingFile}
            onClose={closeFilePicker}
          />
        )}
      </BaseTile>

    </>
  );
};

export default VisualConformanceChecker;
